import { Box, Drawer } from '@mui/material';
import { SIDE_MENU_WIDTH_EXPANDED } from '@/components/common/SideMenu';
import { useVisibleAdminTabs } from './useVisibleAdminTabs';
import { useActiveAdminTab } from './useActiveAdminTab';
import { AdminSideNav } from './AdminSideNav';

interface AdminSideMenuProps {
  /** Controls the temporary (mobile) drawer. Ignored when `permanent` is true. */
  open: boolean;
  /** Fires when the temporary drawer requests to close. */
  onClose: () => void;
  /** Permanent (md+) vs temporary overlay (xs/sm). */
  permanent: boolean;
}

/**
 * The admin-area side menu. Rendered by the app shell (`Layout`) IN PLACE OF the
 * primary `SideMenu` while the user is under `/admin`, so admin is a focused
 * mode with a single sidebar and full-width tool content.
 *
 * Mirrors `SideMenu`'s drawer shell (same width, `top: 56` offset, permanent vs
 * temporary behaviour) so the swap is seamless. The nav body lives in
 * `AdminSideNav`. Unlike the primary menu it does not collapse to an icon rail —
 * the tool labels matter — so the shell hides the collapse control in admin.
 */
export function AdminSideMenu({ open, onClose, permanent }: AdminSideMenuProps) {
  const { groups } = useVisibleAdminTabs();
  const { activeValue } = useActiveAdminTab();
  const width = SIDE_MENU_WIDTH_EXPANDED;

  const content = (
    <Box
      id="primary-navigation"
      role="navigation"
      sx={{
        width,
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: permanent ? 1 : 0,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <AdminSideNav
        groups={groups}
        activeValue={activeValue}
        onNavigate={permanent ? undefined : onClose}
      />
    </Box>
  );

  if (permanent) {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            top: 56,
            height: 'calc(100% - 56px)',
            overflowX: 'hidden',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width, boxSizing: 'border-box' } }}
    >
      {content}
    </Drawer>
  );
}

export default AdminSideMenu;
